import { Injectable } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { ConsumptionType } from 'src/app/classes/consumption-type.class';
import { MaterialColor } from 'src/app/classes/material-color.class';
import { MaterialType } from 'src/app/classes/material-type.class';
import { RawMaterial } from 'src/app/classes/raw-material.class';
import { Supplier } from 'src/app/classes/supplier.class';
import { Material } from '../../classes/material.class';
import { Utils } from '../../utils.class';
import { ApiService } from '../api/api.service';

export interface PlanningMaterialRawMaterialPivot {
    raw_material_id: number;
    consumption: number | null;
    consumption_type_id: number | null;
    optional: boolean;
}

export interface PlanningMaterialPivot {
    id: number;
    material_id: number;
    quantity: number;
    raw_materials: PlanningMaterialRawMaterialPivot[];
}

// Combineert de lichte ids/pivot-data van getForPlanning() met de al geladen materialen-catalogus,
// zoals trackline-planning-materials.component.ts dat ook al client-side doet voor material_raw_materials.pivot.
export function mergePlanningMaterialPivots(catalog: Material[], pivots: PlanningMaterialPivot[]): Material[] {
    return pivots
        .map(pivot => {
            const catalogMaterial = catalog.find(m => m.id === pivot.material_id);

            if (!catalogMaterial) {
                return null;
            }

            const overlayByRawMaterialId = new Map<number, PlanningMaterialRawMaterialPivot>(
                pivot.raw_materials.map(rm => [rm.raw_material_id, rm])
            );

            const rawMaterials: RawMaterial[] = (catalogMaterial.raw_materials ?? [])
                .filter(rm => overlayByRawMaterialId.has(rm.id))
                .map(rm => {
                    const overlay = overlayByRawMaterialId.get(rm.id) as PlanningMaterialRawMaterialPivot;

                    return {
                        ...rm,
                        consumption: overlay.consumption ?? rm.consumption,
                        consumption_type_id: overlay.consumption_type_id ?? rm.consumption_type_id,
                        optional: overlay.optional,
                    };
                });

            const material: Material = {
                ...catalogMaterial,
                raw_materials: rawMaterials,
                planning_material_id: pivot.id,
                quantity: pivot.quantity,
            };

            return material;
        })
        .filter((m): m is Material => m !== null);
}

export interface GroupedOptional {
    materialType: MaterialType;
    optional: boolean;
    rawMaterials: RawMaterial[];
}

// Groepeert de aan het materiaal gekoppelde grondstoffen (nastrooi/coating-achtig) per (via
// material_types_material_types) gerelateerd materiaaltype, zodat overal waar je zo'n optie kiest
// dezelfde keuze ontstaat: al verplicht gekoppeld -> geen keuze (wordt elders altijd meegenomen),
// al optioneel gekoppeld -> vast als enige keuze (aan/uit), nog niets gekoppeld -> vrij kiezen uit
// de volledige lijst van dat type.
export function groupOptionalsByRelatedType(
    linkedMaterials: RawMaterial[],
    mainType: MaterialType,
    materialTypes: MaterialType[],
    rawMaterials: RawMaterial[]
): GroupedOptional[] {
    const groups: GroupedOptional[] = [];

    for (const materialType of materialTypes) {
        if (materialType.hidden) {
            continue;
        }

        const relatedType = mainType?.related?.find(mtr => mtr.related_material_type_id === materialType.id);
        const linked = linkedMaterials.filter(rm => rm.material_type_id === materialType.id);

        if (linked.some(rm => !rm.pivot?.optional)) {
            // Al verplicht aan het artikel gekoppeld: geen keuze te maken, wordt altijd meegenomen.
            continue;
        }

        if (!relatedType && !linked.length) {
            continue;
        }

        const raws = rawMaterials
            .filter(rm => rm.material_type_id === materialType.id && !rm.deleted_at)
            .sort((a, b) => Utils.sortName(a.name, b.name));

        groups.push({
            materialType,
            optional: relatedType?.optional ?? true,
            rawMaterials: linked.length ? linked : raws,
        });
    }

    return groups;
}

export function isOptionalSelectionValid(
    groupedOptionals: GroupedOptional[],
    selectedOptionalsMap: Map<number, RawMaterial | RawMaterial[]>
): boolean {
    return groupedOptionals.every(group => {
        if (group.optional) {
            return true;
        }

        const val = selectedOptionalsMap.get(group.materialType.id);

        if (group.materialType.single) {
            return val != null;
        }

        return Array.isArray(val) && val.length > 0;
    });
}

export function flattenSelectedOptionals(
    selectedOptionalsMap: Map<number, RawMaterial | RawMaterial[]>
): RawMaterial[] {
    const selected: RawMaterial[] = [];

    selectedOptionalsMap.forEach(val => {
        if (Array.isArray(val)) {
            selected.push(...val);
        } else if (val) {
            selected.push(val);
        }
    });

    return selected;
}

@Injectable({ providedIn: 'root' })
export class MaterialService {
    constructor(private apiService: ApiService) {}

    public getList(withTrashed = false, includeIncomplete = false): Observable<Material[]> {
        return this.apiService
            .getCallData$<Material[]>('materials', includeIncomplete ? { all: 1 } : {})
            .pipe(shareReplay(1))
            .pipe(
                map(materials => {
                    materials.sort((a, b) => (b.plannings_count ?? 0) - (a.plannings_count ?? 0));
                    return withTrashed
                        ? materials
                        : materials
                              .filter(m => !m.deleted_at)
                              .map(m => ({
                                  ...m,
                                  raw_materials: m.raw_materials?.filter(r => !r.deleted_at && !r.pivot?.deleted_at),
                              }));
                })
            );
    }

    public getForPlanning(planningId: number): Observable<PlanningMaterialPivot[]> {
        return this.apiService.getCallData$<PlanningMaterialPivot[]>(`materials/planning/${planningId}`);
    }

    public getOptionalsByMaterial(materialId: number): Observable<RawMaterial[]> {
        return this.apiService.getCallData$<RawMaterial[]>(`materials/optionals/${materialId}`);
    }

    public getAllRawMaterials(): Observable<RawMaterial[]> {
        return combineLatest([
            this.apiService.getCallData$<RawMaterial[]>('raw-materials'),
            this.getMaterialTypes(),
            this.getColors(),
            this.getSuppliers(),
            this.getConsumptionTypes(),
        ]).pipe(
            map(([rawMaterials, materialTypes, colors, suppliers, consumptionTypes]) => {
                const materialTypesById = new Map(materialTypes.map(mt => [mt.id, mt]));
                const colorsById = new Map(colors.map(c => [c.id, c]));
                const suppliersById = new Map(suppliers.map(s => [s.id, s]));
                const consumptionTypesById = new Map(consumptionTypes.map(ct => [ct.id, ct]));

                return rawMaterials.map(rawMaterial => ({
                    ...rawMaterial,
                    material_type: materialTypesById.get(rawMaterial.material_type_id),
                    color: colorsById.get(rawMaterial.color_id),
                    supplier: suppliersById.get(rawMaterial.supplier_id),
                    consumption_type: consumptionTypesById.get(rawMaterial.consumption_type_id),
                }));
            }),
            shareReplay(1)
        );
    }

    public getColors(): Observable<MaterialColor[]> {
        return this.apiService.getCallData$<MaterialColor[]>('material-colors').pipe(shareReplay(1));
    }

    public getSuppliers(): Observable<Supplier[]> {
        return this.apiService.getCallData$<Supplier[]>('suppliers').pipe(shareReplay(1));
    }

    public getConsumptionTypes(): Observable<ConsumptionType[]> {
        return this.apiService.getCallData$<ConsumptionType[]>('consumption-types').pipe(shareReplay(1));
    }

    public addRawMaterialToMaterial(materialId: number, rawMaterialId: number): Observable<any> {
        return this.apiService.postCall$(`materials/raw-materials`, {
            raw_material_id: rawMaterialId,
            material_id: materialId,
        });
    }

    public deleteRawMaterialFromMaterial(materialId: number, rawMaterialId: number): Observable<any> {
        return this.apiService.deleteCall$(`materials/raw-materials`, { rawMaterialId, materialId });
    }

    public updateRawMaterial(
        rawMaterialId: number,
        fields: Partial<Pick<RawMaterial, 'thickness' | 'consumption' | 'width' | 'consumption_type_id' | 'color_id'>>
    ): Observable<any> {
        return this.apiService.putCall$(`raw-materials/${rawMaterialId}`, fields);
    }

    public toggleOptional(pivot: any, optional: number): Observable<any> {
        return this.apiService.postCall$(`material-raw-materials/toggle-optional`, {
            material_id: pivot.material_id,
            raw_material_id: pivot.raw_material_id,
            optional: optional,
        });
    }

    public updatePivotConsumption(pivot: any, consumption: number | null): Observable<any> {
        return this.apiService.postCall$(`material-raw-materials/update-consumption`, {
            material_id: pivot.material_id,
            raw_material_id: pivot.raw_material_id,
            consumption: consumption,
        });
    }

    public getMaterialTypes(): Observable<MaterialType[]> {
        return this.apiService.getCallData$<MaterialType[]>('material-types').pipe(shareReplay(1));
    }
}
